import { Navigate, useParams } from 'react-router-dom';
import {
  Box,
} from '@mui/material';
import { useAuthentication } from '../store/AppContext';
import {
  PilotPreferencesManager,
} from '../components/common';
import { useUserProfile } from '../hooks';
import { pl } from '../i18n';
import { CompanyApplicationsSection, PilotApplicationsListSection } from '../features/account/applications';
import { AccountSettingsSection, EditProfileSection } from '../features/account/profileSettings';
import {
  AccountDashboardSection,
  AccountLayout,
  CompanyListingsOffersSection,
  FavouritesSection,
  MessagesSection,
  MyListingsSection,
} from '../features/account/shell';
export { AddListingPage } from './account/AddListingPage';
export { EditListingPage } from './account/EditListingPage';

// ============================================================
// ACCOUNT DASHBOARD
// ============================================================

export function AccountDashboardPage() {
  return (
    <AccountLayout pageTitle={pl.account.dashboard}>
      <AccountDashboardSection />
    </AccountLayout>
  );
}

// ============================================================
// MY LISTINGS PAGE
// ============================================================

export function MyListingsPage() {
  const { currentUser } = useAuthentication();

  if (currentUser?.membership?.USER_TYPE === 'TRANSPORT_COMPANY') {
    return <Navigate to="/account/listings-and-offers?tab=listings" replace />;
  }

  return <MyListingsSection />;
}

// ============================================================
// FAVOURITES PAGE
// ============================================================

export function FavouritesPage() {
  return (
    <AccountLayout pageTitle={pl.account.favourites}>
      <FavouritesSection />
    </AccountLayout>
  );
}

// ============================================================
// MESSAGES PAGE
// ============================================================

export function MessagesPage() {
  return (
    <AccountLayout pageTitle="Wiadomości">
      <MessagesSection />
    </AccountLayout>
  );
}

// ============================================================
// SENT APPLICATIONS PAGE (PILOT)
// ============================================================

export function SentApplicationsPage() {
  return (
    <AccountLayout pageTitle={pl.account.sentApplications}>
      <PilotApplicationsListSection
      emptyTitle={pl.account.noSentApplicationsTitle}
      emptyDescription={pl.account.noSentApplicationsDescription}
      loadingMessage={pl.account.loadingSentApplications}
      showAssignedClosedOnly={false}
      />
    </AccountLayout>
  );
}

export function MyOrdersPage() {
  return (
    <AccountLayout pageTitle={pl.account.myOrders}>
      <PilotApplicationsListSection
      emptyTitle={pl.account.noMyOrdersTitle}
      emptyDescription={pl.account.noMyOrdersDescription}
      loadingMessage={pl.account.loadingMyOrders}
      showAssignedClosedOnly
      />
    </AccountLayout>
  );
}

// ============================================================
// RECEIVED APPLICATIONS PAGE (COMPANY)
// ============================================================

export function ReceivedApplicationsPage() {
  const { listingId } = useParams<{ listingId?: string }>();

  if (!listingId) {
    return <Navigate to="/account/listings-and-offers?tab=received" replace />;
  }

  return (
    <AccountLayout pageTitle="Nadesłane oferty">
      <CompanyApplicationsSection
        listingStatus="OPEN"
        emptyTitle="Brak nadesłanych ofert"
        emptyDescription="Nikt jeszcze nie zaaplikował na Twoje otwarte ogłoszenia."
        detailBasePath="/account/received-applications"
        backPath="/account/received-applications"
        showStartAction
      />
    </AccountLayout>
  );
}

export function OrdersPage() {
  const { listingId } = useParams<{ listingId?: string }>();

  if (!listingId) {
    return <Navigate to="/account/listings-and-offers?tab=orders" replace />;
  }

  return (
    <AccountLayout pageTitle="Zlecenia">
      <CompanyApplicationsSection
        listingStatus="CLOSED"
        emptyTitle="Brak zleceń"
        emptyDescription="Nie masz jeszcze zamkniętych ogłoszeń, które zostały przekształcone w zlecenia."
        detailBasePath="/account/orders"
        backPath="/account/orders"
        showStartAction={false}
      />
    </AccountLayout>
  );
}

export function CompanyListingsOffersPage() {
  return <CompanyListingsOffersSection />;
}

// ============================================================
// SETTINGS PAGE
// ============================================================

// ============================================================
// PILOT PREFERENCES PAGE
// ============================================================

export function PilotPreferencesPage() {
  const { currentUser } = useAuthentication();
  const { data: profile } = useUserProfile();

  const isPilot = currentUser?.membership?.USER_TYPE === 'PILOT_B2B_COMPANY';

  if (!isPilot) {
    return <Navigate to="/account" replace />;
  }

  return (
    <AccountLayout pageTitle="Preferowane lokalizacje">
      <Box sx={{ maxWidth: 800 }}>
        {profile && (
          <PilotPreferencesManager pilotId={String(currentUser?.membership?.pilot?.documentId ?? "")} />
        )}
      </Box>
    </AccountLayout>
  );
}

// ============================================================
// EDIT PROFILE PAGE
// ============================================================

interface EditProfilePageProps {
  embedded?: boolean;
}

export function EditProfilePage({ embedded = false }: EditProfilePageProps) {
  if (!embedded) {
    return <Navigate to="/account/settings?tab=profile" replace />;
  }
  return <EditProfileSection embedded={embedded} />;
}

export function AccountSettingsPage() {
  return (
    <AccountLayout pageTitle="Ustawienia konta">
      <AccountSettingsSection />
    </AccountLayout>
  );
}
